import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CompassLayout from "@/components/compass/CompassLayout";
import { useCompass } from "@/context/CompassContext";
import { ArrowRight } from "@phosphor-icons/react";

// Path A: Tengo una Idea
const QUESTIONS = [
  {
    key: "motivo",
    question: "¿Por qué quieres emprender?",
    options: [
      "Ingresos adicionales",
      "Independencia",
      "Pasión personal",
      "Negocio familiar",
      "Construir patrimonio",
      "Otro",
    ],
  },
  {
    key: "experiencia",
    question: "¿Qué experiencia tienes?",
    options: [
      "Ninguna",
      "Algo de experiencia",
      "Mucha experiencia",
      "Ya trabajé en esta industria",
    ],
  },
  {
    key: "tiempo",
    question: "¿Cuánto tiempo puedes dedicar?",
    options: ["Tiempo completo", "Medio tiempo", "Solo fines de semana"],
  },
  {
    key: "capital",
    question: "¿Cuál es tu capacidad de inversión?",
    options: [
      "Muy limitada",
      "Limitada",
      "Moderada",
      "Alta",
      "Tengo acceso a financiamiento o inversionistas",
    ],
  },
  {
    key: "recursos",
    question: "¿Qué recursos ya tienes?",
    multi: true,
    options: [
      "Local",
      "Clientes",
      "Equipo",
      "Inventario",
      "Contactos",
      "Experiencia",
      "Ninguno",
    ],
  },
  {
    key: "industria_interes",
    question: "¿Hay alguna industria que te llame la atención? (opcional)",
    optional: true,
    input: true,
    placeholder: "Ej. moda, gastronomía, tecnología…",
  },
];

export default function CompassIdea() {
  const nav = useNavigate();
  const { draft, setAnswer } = useCompass();

  useEffect(() => {
    if (!draft.city) { nav("/brujula/ciudad", { replace: true }); return; }
    if (draft.path !== "idea") { nav("/brujula", { replace: true }); }
  }, [draft, nav]);

  const answered = QUESTIONS.filter((q) => !q.optional).every((q) => {
    const v = draft.answers[q.key];
    if (q.multi) return Array.isArray(v) && v.length > 0;
    return !!v;
  });

  const submit = () => nav("/brujula/analizando");

  const toggleMulti = (key, opt) => {
    const cur = Array.isArray(draft.answers[key]) ? draft.answers[key] : [];
    if (opt === "Ninguno") { setAnswer(key, ["Ninguno"]); return; }
    let next = cur.filter((x) => x !== "Ninguno");
    next = next.includes(opt) ? next.filter((x) => x !== opt) : [...next, opt];
    setAnswer(key, next);
  };

  return (
    <CompassLayout back="/brujula/ciudad" step={3} total={4} testid="page-compass-idea">
      <div className="mb-6">
        <div className="label-mono text-muted-foreground">Paso 3 de 4 · Tengo una Idea</div>
        <h1 className="display text-3xl md:text-4xl mt-2 leading-[1]">Cuéntame más</h1>
      </div>

      <div className="space-y-6">
        {QUESTIONS.map((q, idx) => (
          <div key={q.key} data-testid={`q-${q.key}`}>
            <div className="flex items-start gap-2 mb-2">
              <div className="label-mono text-muted-foreground">P{idx + 1}</div>
              <div className="font-semibold text-base leading-snug">
                {q.question}
                {q.multi && <span className="label-mono ml-2 text-muted-foreground">(múltiple)</span>}
              </div>
            </div>

            {q.input ? (
              <input
                className="input-brut !py-3"
                placeholder={q.placeholder}
                value={draft.answers[q.key] || ""}
                onChange={(e) => setAnswer(q.key, e.target.value)}
                data-testid={`input-${q.key}`}
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {q.options.map((opt) => {
                  const selected = q.multi
                    ? Array.isArray(draft.answers[q.key]) && draft.answers[q.key].includes(opt)
                    : draft.answers[q.key] === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => q.multi ? toggleMulti(q.key, opt) : setAnswer(q.key, opt)}
                      data-testid={`opt-${q.key}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`text-left border-2 border-foreground p-3 text-sm transition-colors ${
                        selected ? "bg-foreground text-background" : "bg-surface hover:bg-muted"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={submit}
        disabled={!answered}
        data-testid="btn-idea-continuar"
        className="btn-primary mt-8 w-full flex items-center justify-center gap-2 !py-4 !text-base"
      >
        Analizar mi ruta <ArrowRight size={16} weight="bold" />
      </button>
      {!answered && (
        <div className="text-xs text-muted-foreground mt-2 text-center">Responde todas las preguntas obligatorias para continuar.</div>
      )}
    </CompassLayout>
  );
}
